import { indexMap, parseCellId, type CellType, type MapDef } from '@brig/rules-engine';
import { drawProps } from './terrainProps.ts';
import type { TerrainSprites } from './terrainSprites.ts';

/**
 * The board's terrain, painted in the manner of a strategy-game map and rendered with real
 * relief in the isometric view: a bright azure sea with turquoise shallows, white surf and
 * wave strokes and a faint white grid, red reef squares, and islands that rise out of it on
 * pale limestone cliffs to grassy tops and rocky peaks. Painted props — cliff faces along
 * the shores, trees, villages, piers and rocks — stand on it (see terrainProps.ts).
 *
 * Islands follow the rules' cells: `land` cells are land, `coast` cells ("partly land, water
 * covers half the cell or more") get the island's edge, and land never spills into sea,
 * port or reef cells beyond a hair at their edges. Everything is procedural and seeded by
 * the map id, so a map always looks the same.
 */

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Seeded 2D value noise in [0, 1] with smooth interpolation, and fractal sums of it. */
export function makeNoise(seed: number) {
  const lattice = (ix: number, iy: number) => {
    let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ seed;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
  };
  const value = (x: number, y: number) => {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const a = lattice(ix, iy);
    const b = lattice(ix + 1, iy);
    const c = lattice(ix, iy + 1);
    const d = lattice(ix + 1, iy + 1);
    return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
  };
  const fbm = (x: number, y: number, octaves: number) => {
    let sum = 0;
    let amp = 0.5;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amp * value(x, y);
      norm += amp;
      x = x * 2.03 + 17.1;
      y = y * 2.03 + 3.7;
      amp *= 0.5;
    }
    return sum / norm;
  };
  return { value, fbm };
}

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// ---------------------------------------------------------------------------
// The land field
// ---------------------------------------------------------------------------

/** How much land each cell type wants: coast is a partial island edge. */
const LAND_WEIGHT: Record<CellType, number> = {
  land: 1,
  coast: 0.42,
  sea: 0,
  port: 0,
  royal_port: 0,
  reef: 0,
};

/** The threshold of `landPotential`: above it is land. */
export const SHORE = 0.5;

/**
 * Land potential at a point in cell units (u east, v south): the cells' land weights blended
 * smoothly between cell centres, roughened by noise, then held to the rules — solid land in
 * `land` cells and at most a hair of it in water cells.
 */
export function landPotential(map: MapDef, seed = hashSeed(map.id)) {
  const idx = indexMap(map);
  const { fbm } = makeNoise(seed);
  const weights = new Float32Array(map.width * map.height);
  const types: CellType[] = [];
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const t = idx.type({ x, y });
      types.push(t);
      weights[y * map.width + x] = LAND_WEIGHT[t];
    }
  }
  const isLand = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < map.width && y < map.height && types[y * map.width + x] === 'land';
  /**
   * Where the shore runs, as an offset from the edge of the land cells in cell units: positive
   * pushes a cape out into the coast, negative bites a bay into the land. It wanders along the
   * coast, so an island is not just its block of land cells with an even beach round it.
   */
  const shoreAt = (u: number, v: number): number => {
    const s =
      -0.07 +
      0.55 * smoothstep(0.28, 0.72, fbm(u * 0.9 + 17, v * 0.9 - 5, 3)) +
      (fbm(u * 3.1 + 31, v * 3.1 - 7, 2) - 0.5) * 0.12;
    return Math.min(0.46, Math.max(-0.07, s));
  };
  /**
   * How far a coast point is from the land beside it, stretched where land lies on more than
   * one side so that water still covers at least half the cell.
   */
  const coastDistance = (cx: number, cy: number, lx: number, ly: number): number => {
    const sides: number[] = [];
    if (isLand(cx - 1, cy)) sides.push(lx);
    if (isLand(cx + 1, cy)) sides.push(1 - lx);
    if (isLand(cx, cy - 1)) sides.push(ly);
    if (isLand(cx, cy + 1)) sides.push(1 - ly);
    const corners: number[] = [];
    if (isLand(cx - 1, cy - 1)) corners.push(Math.hypot(lx, ly));
    if (isLand(cx + 1, cy - 1)) corners.push(Math.hypot(1 - lx, ly));
    if (isLand(cx - 1, cy + 1)) corners.push(Math.hypot(lx, 1 - ly));
    if (isLand(cx + 1, cy + 1)) corners.push(Math.hypot(1 - lx, 1 - ly));
    const stretch = sides.length <= 1 ? 1 : sides.length === 2 ? 1.8 : 2.6;
    return Math.min(...sides, ...corners.map((c) => c * 0.8), Infinity) * stretch;
  };
  /** How far a land point is from the nearest side its cell shares with water. */
  const landDistance = (cx: number, cy: number, lx: number, ly: number): number => {
    const sides = [Infinity];
    if (!isLand(cx - 1, cy)) sides.push(lx);
    if (!isLand(cx + 1, cy)) sides.push(1 - lx);
    if (!isLand(cx, cy - 1)) sides.push(ly);
    if (!isLand(cx, cy + 1)) sides.push(1 - ly);
    return Math.min(...sides);
  };
  const w = (x: number, y: number) => {
    const cx = Math.min(map.width - 1, Math.max(0, x));
    const cy = Math.min(map.height - 1, Math.max(0, y));
    return weights[cy * map.width + cx] ?? 0;
  };
  return (u: number, v: number): number => {
    const gx = u - 0.5;
    const gy = v - 0.5;
    const ix = Math.floor(gx);
    const iy = Math.floor(gy);
    const fx = smoothstep(0, 1, gx - ix);
    const fy = smoothstep(0, 1, gy - iy);
    const top = w(ix, iy) + (w(ix + 1, iy) - w(ix, iy)) * fx;
    const bottom = w(ix, iy + 1) + (w(ix + 1, iy + 1) - w(ix, iy + 1)) * fx;
    let p = top + (bottom - top) * fy;
    p += (fbm(u * 1.3, v * 1.3, 3) - 0.5) * 0.34 + (fbm(u * 4.5, v * 4.5, 2) - 0.5) * 0.12;

    const cx = Math.min(map.width - 1, Math.max(0, Math.floor(u)));
    const cy = Math.min(map.height - 1, Math.max(0, Math.floor(v)));
    const type = types[cy * map.width + cx] ?? 'sea';
    const lx = u - cx;
    const ly = v - cy;
    const edge = Math.min(lx, 1 - lx, ly, 1 - ly);
    if (type === 'land') {
      p = Math.max(p, SHORE + 0.12 * smoothstep(0.04, 0.16, edge));
      p = Math.min(p, SHORE + (landDistance(cx, cy, lx, ly) + shoreAt(u, v)) * 3);
    } else if (type !== 'coast') p = Math.min(p, SHORE - 0.3 * smoothstep(0.03, 0.14, edge));
    else {
      // The shore line, not the blend of cell weights, draws the island's edge here.
      const d = coastDistance(cx, cy, lx, ly);
      p = SHORE + Math.min(0.3, Math.max(-0.3, (shoreAt(u, v) - d) * 3));
    }
    return p;
  };
}

/**
 * How close a point is to land, 0 far out to 1 on it: the cells' land blurred over about two
 * cells, for the shallows that ring the islands.
 */
export function nearLand(map: MapDef) {
  const idx = indexMap(map);
  const W = map.width;
  const Hh = map.height;
  const base = new Float32Array(W * Hh);
  for (let y = 0; y < Hh; y++)
    for (let x = 0; x < W; x++) {
      const t = idx.type({ x, y });
      base[y * W + x] = t === 'land' ? 1 : t === 'coast' ? 0.55 : 0;
    }
  // Separable box blur, twice (close to a Gaussian), radius 1 cell.
  const blur = (src: Float32Array) => {
    const a = new Float32Array(W * Hh);
    const b = new Float32Array(W * Hh);
    for (let y = 0; y < Hh; y++)
      for (let x = 0; x < W; x++) {
        let sum = 0;
        for (let k = -1; k <= 1; k++) sum += src[y * W + Math.min(W - 1, Math.max(0, x + k))] ?? 0;
        a[y * W + x] = sum / 3;
      }
    for (let y = 0; y < Hh; y++)
      for (let x = 0; x < W; x++) {
        let sum = 0;
        for (let k = -1; k <= 1; k++) sum += a[Math.min(Hh - 1, Math.max(0, y + k)) * W + x] ?? 0;
        b[y * W + x] = sum / 3;
      }
    return b;
  };
  const field = blur(blur(base));
  const f = (x: number, y: number) =>
    field[Math.min(Hh - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x))] ?? 0;
  return (u: number, v: number) => {
    const gx = u - 0.5;
    const gy = v - 0.5;
    const ix = Math.floor(gx);
    const iy = Math.floor(gy);
    const fx = smoothstep(0, 1, gx - ix);
    const fy = smoothstep(0, 1, gy - iy);
    const top = f(ix, iy) + (f(ix + 1, iy) - f(ix, iy)) * fx;
    const bottom = f(ix, iy + 1) + (f(ix + 1, iy + 1) - f(ix, iy + 1)) * fx;
    return Math.min(1, (top + (bottom - top) * fy) * 1.6);
  };
}

// ---------------------------------------------------------------------------
// Fields: elevation and colour on the map plane
// ---------------------------------------------------------------------------

type RGB = readonly [number, number, number];

const hex = (h: string): RGB => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];

/** The palette. */
export const PALETTE = {
  seaDark: hex('#2A7DC2'),
  sea: hex('#3A94D6'),
  seaLight: hex('#5AB2E6'),
  shallow: hex('#48C2D8'),
  lagoon: hex('#86DCD8'),
  foam: hex('#F4FBF8'),
  contour: hex('#BDEDEA'),
  glint: hex('#FFFFFF'),
  reef: hex('#DA3F4A'),
  grid: [236, 248, 255] as RGB,
  sand: hex('#E9DDB0'),
  grass: hex('#A3C65C'),
  grassDark: hex('#7FA844'),
  meadow: hex('#BCD272'),
  rock: hex('#D9D4C7'),
  rockMid: hex('#B3AD9F'),
  rockDark: hex('#7C766B'),
} as const;

const mix = (a: RGB, b: RGB, t: number): [number, number, number] => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/** Highest land, in cells: how far a peak rises above the sea. */
export const MAX_ELEVATION = 1.2;

export interface TerrainFields {
  /** Samples per cell. */
  readonly res: number;
  readonly width: number;
  readonly height: number;
  /** Height above the sea in cells, 0 on the water. */
  readonly elevation: Float32Array;
  /** Lit colour, RGB per sample. */
  readonly colour: Uint8ClampedArray;
  /** `landPotential` per sample: above SHORE is land. */
  readonly potential: Float32Array;
}

/**
 * Elevation and colour of the whole map at `res` samples per cell. Land rises from the beach
 * into wooded hills with the odd rocky peak; it stays low round the forts, so they stand on
 * the shore. The sea is flat and carries the grid. Light comes from the south-west, so the
 * slopes facing the isometric camera are lit.
 */
export function terrainFields(map: MapDef, res = 28): TerrainFields {
  const seed = hashSeed(map.id);
  const potential = landPotential(map, seed);
  const near = nearLand(map);
  const { value, fbm } = makeNoise(seed ^ 0x9e3779b9);
  const idx = indexMap(map);
  const c = PALETTE;
  const W = map.width * res;
  const H = map.height * res;
  const pField = new Float32Array(W * H);
  const elevation = new Float32Array(W * H);
  const colour = new Uint8ClampedArray(W * H * 3);
  const forts = map.forts.map((f) => parseCellId(f.cell)).filter((p) => p !== null);

  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      const u = (px + 0.5) / res;
      const v = (py + 0.5) / res;
      const i = py * W + px;
      const p = potential(u, v);
      pField[i] = p;
      if (p <= SHORE) continue;
      // Islands rise sheer out of the sea, higher on some stretches of coast than others,
      // onto hilly tops with rocky peaks. Heights in cells.
      const hills = fbm(u * 1.3 - 2, v * 1.3 + 8, 3);
      const peaks = smoothstep(0.5, 0.85, fbm(u * 0.75 + 5, v * 0.75 - 3, 4));
      const knolls = fbm(u * 5 + 1, v * 5 - 4, 2);
      const bluff = fbm(u * 0.8 + 23, v * 0.8 - 11, 2);
      const cliff = smoothstep(SHORE + 0.004, SHORE + 0.05, p);
      const inland = smoothstep(SHORE + 0.05, 0.9, p);
      let e =
        cliff * (0.18 + 0.27 * smoothstep(0.3, 0.7, bluff) + 0.04 * knolls) +
        inland * (0.28 * hills + 0.5 * peaks);
      let fortNear = Infinity;
      for (const f of forts) fortNear = Math.min(fortNear, Math.hypot(u - f.x - 0.5, v - f.y - 0.5));
      e *= 0.3 + 0.7 * smoothstep(0.55, 1.5, fortNear);
      elevation[i] = Math.min(MAX_ELEVATION, e);
    }
  }

  const light = (() => {
    const l = [-0.3, 0.75, 0.9];
    const n = Math.hypot(l[0] ?? 0, l[1] ?? 0, l[2] ?? 0);
    return l.map((x) => x / n) as [number, number, number];
  })();

  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      const i = py * W + px;
      const u = (px + 0.5) / res;
      const v = (py + 0.5) / res;
      const p = pField[i] ?? 0;
      let col: [number, number, number];

      if (p <= SHORE) {
        // -- sea: mottled azure, wide shallows in bands, sandy water at the beach --
        const mottle = fbm(u * 0.6 + 11, v * 0.6, 4);
        const fine = fbm(u * 2.4, v * 2.4, 3);
        col = mix(c.seaDark, c.seaLight, smoothstep(0.3, 0.72, mottle * 0.75 + fine * 0.25));
        col = mix(col, c.sea, 0.35);
        const depth = Math.max(near(u, v), smoothstep(0, SHORE, p) * 0.9) + (fine - 0.5) * 0.12;
        col = mix(col, c.shallow, smoothstep(0.16, 0.4, depth) * 0.9);
        col = mix(col, c.lagoon, smoothstep(0.45, 0.75, depth) * 0.7);
        // A pale line along the edge of the shallows, like a depth contour.
        col = mix(col, c.contour, 0.35 * Math.exp(-(((depth - 0.4) / 0.025) ** 2)));
        // White surf breaking along the shore, with a second, broken line just off it.
        const surf = smoothstep(SHORE - 0.035, SHORE - 0.004, p);
        const swash = Math.exp(-(((p - (SHORE - 0.075)) / 0.012) ** 2)) * smoothstep(0.45, 0.6, fine);
        col = mix(col, c.foam, Math.max(surf * 0.95, swash * 0.6));
        // White wave strokes on the open water: short, curved, scattered.
        const stroke = value(u * 1.1 + fine * 1.8, v * 7 + fine * 2.5);
        col = mix(col, c.glint, smoothstep(0.84, 0.95, stroke) * 0.7 * (1 - smoothstep(0.2, 0.45, depth)));
        const cx = Math.min(map.width - 1, Math.floor(u));
        const cy = Math.min(map.height - 1, Math.floor(v));
        const type = idx.type({ x: cx, y: cy });
        if (type === 'reef') {
          const lx = u - cx;
          const ly = v - cy;
          const edge = Math.min(lx, 1 - lx, ly, 1 - ly);
          col = mix(col, c.reef, 0.82 * smoothstep(0.05, 0.08, edge));
        }
        // The grid: thin, faint white lines.
        const gx = Math.abs(u - Math.round(u));
        const gy = Math.abs(v - Math.round(v));
        const line = 1 - smoothstep(0.6 / res, 1.4 / res, Math.min(gx, gy));
        col = mix(col, c.grid, 0.26 * line);
      } else {
        // -- land: sand, grass and woods, rock where it is steep or high --
        const e = elevation[i] ?? 0;
        const ex = ((elevation[i + 1] ?? e) - (elevation[i - 1] ?? e)) * (res / 2);
        const ey = ((elevation[i + W] ?? e) - (elevation[i - W] ?? e)) * (res / 2);
        const nl = Math.hypot(ex, ey, 1);
        const shade = (-ex * light[0] - ey * light[1] + light[2]) / nl;
        const steep = Math.hypot(ex, ey);
        const strata = value(u * 2.5 + v, (v - u * 0.5) * 6);
        // Grass in patches of meadow and darker turf; the trees are drawn over it (see props).
        const patch = fbm(u * 3 + 7, v * 3, 3);
        let lc = mix(c.grassDark, c.grass, smoothstep(0.3, 0.55, patch));
        lc = mix(lc, c.meadow, smoothstep(0.6, 0.75, patch) * 0.8);
        const rockiness = Math.max(
          smoothstep(0.45, 0.8, steep + (strata - 0.5) * 0.2),
          smoothstep(0.62, 0.85, e / MAX_ELEVATION),
        );
        lc = mix(lc, mix(c.rock, c.rockMid, smoothstep(0.3, 0.7, strata) * 0.6), rockiness);
        // The cliff's lip: a rim of pale stone round the top.
        lc = mix(lc, c.rock, (1 - smoothstep(SHORE + 0.02, SHORE + 0.06, p)) * 0.85);
        const lit = 0.55 + 0.6 * Math.max(0, shade);
        col = [lc[0] * lit, lc[1] * lit, lc[2] * lit];
      }
      colour[i * 3] = col[0];
      colour[i * 3 + 1] = col[1];
      colour[i * 3 + 2] = col[2];
    }
  }
  return { res, width: W, height: H, elevation, colour, potential: pField };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/** The map seen from above, unlit by relief: for the minimap. */
export function flatTerrain(fields: TerrainFields): HTMLCanvasElement {
  const img = new ImageData(fields.width, fields.height);
  for (let i = 0; i < fields.width * fields.height; i++) {
    img.data[i * 4] = fields.colour[i * 3] ?? 0;
    img.data[i * 4 + 1] = fields.colour[i * 3 + 1] ?? 0;
    img.data[i * 4 + 2] = fields.colour[i * 3 + 2] ?? 0;
    img.data[i * 4 + 3] = 255;
  }
  return toCanvas(img);
}

export interface IsoTerrain {
  readonly canvas: HTMLCanvasElement;
  /** Where the canvas goes in isometric view space, in cells (view = K·(x−y), K·(x+y)/2 − z). */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

const K = Math.SQRT1_2;

/**
 * The terrain in the isometric view with its relief: each pixel column of the view is a line
 * x − y = const across the map, walked from the near edge to the far one; a sample is drawn
 * from its projected top down to the highest pixel drawn so far (the "voxel space" way), so
 * hills stand up out of the sea and hide what lies behind them.
 */
export function isoTerrain(
  map: MapDef,
  fields: TerrainFields,
  sprites: TerrainSprites,
  pxPerCell = 96,
): IsoTerrain {
  const { res, width: W, height: H, elevation, colour, potential } = fields;
  const { value } = makeNoise(hashSeed(map.id) ^ 0x51ed27);
  const { rock, rockMid, rockDark } = PALETTE;
  const Wc = map.width;
  const Hc = map.height;
  const x0 = -Hc * K;
  const y0 = -MAX_ELEVATION;
  const viewW = (Wc + Hc) * K;
  const viewH = ((Wc + Hc) * K) / 2 + MAX_ELEVATION;
  const cw = Math.ceil(viewW * pxPerCell);
  const ch = Math.ceil(viewH * pxPerCell);
  const img = new ImageData(cw, ch);
  const data = img.data;
  const ds = 1 / (K * pxPerCell * 1.5);

  const elev = (x: number, y: number) => {
    const fx = x * res - 0.5;
    const fy = y * res - 0.5;
    const ix = Math.max(0, Math.min(W - 2, Math.floor(fx)));
    const iy = Math.max(0, Math.min(H - 2, Math.floor(fy)));
    const tx = Math.min(1, Math.max(0, fx - ix));
    const ty = Math.min(1, Math.max(0, fy - iy));
    const a = elevation[iy * W + ix] ?? 0;
    const b = elevation[iy * W + ix + 1] ?? 0;
    const c = elevation[(iy + 1) * W + ix] ?? 0;
    const d = elevation[(iy + 1) * W + ix + 1] ?? 0;
    return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
  };

  for (let col = 0; col < cw; col++) {
    const vx = x0 + (col + 0.5) / pxPerCell;
    const cdiff = vx / K; // x − y along this column
    const sMin = Math.abs(cdiff);
    const sMax = Math.min(2 * Wc - cdiff, 2 * Hc + cdiff);
    // Nothing below the near edge of the map at sea level (else the first sample would
    // paint a curtain down to the bottom of the canvas).
    let ybuf = Math.min(ch, Math.floor(((K * sMax) / 2 - y0) * pxPerCell) + 1);
    for (let s = sMax; s >= sMin; s -= ds) {
      const x = (s + cdiff) / 2;
      const y = (s - cdiff) / 2;
      const e = elev(x, y);
      const sy = Math.floor(((K * s) / 2 - e - y0) * pxPerCell);
      if (sy >= ybuf) continue;
      const si = Math.min(H - 1, Math.floor(y * res)) * W + Math.min(W - 1, Math.floor(x * res));
      const r = colour[si * 3] ?? 0;
      const g = colour[si * 3 + 1] ?? 0;
      const b = colour[si * 3 + 2] ?? 0;
      for (let py = Math.max(0, sy); py < ybuf; py++) {
        const o = (py * cw + col) * 4;
        if (py === sy || e < 0.004) {
          data[o] = r;
          data[o + 1] = g;
          data[o + 2] = b;
        } else if ((potential[si] ?? 0) > SHORE + 0.07) {
          // A hillside inland: the ground it belongs to, in shade.
          data[o] = r * 0.93;
          data[o + 1] = g * 0.93;
          data[o + 2] = b * 0.93;
        } else {
          // Below the sample's top lies the cliff it reveals: pale limestone streaked
          // downwards, darkening towards the waterline.
          const down = (py - sy) / pxPerCell;
          const streak = value(col * 0.21, py * 0.012) * 0.7 + value(col * 0.9, py * 0.05) * 0.3;
          const face = mix(
            mix(rockMid, rock, smoothstep(0.35, 0.65, streak)),
            rockDark,
            smoothstep(0.03, 0.25, down) * 0.55,
          );
          const crack = smoothstep(0.78, 0.86, value(col * 0.45 + 9, py * 0.02)) * 0.35;
          data[o] = face[0] * (1 - crack);
          data[o + 1] = face[1] * (1 - crack);
          data[o + 2] = face[2] * (1 - crack);
        }
        data[o + 3] = 255;
      }
      ybuf = Math.max(0, sy);
    }
  }
  const canvas = toCanvas(img);
  const ctx = canvas.getContext('2d');
  if (ctx) {
    drawProps(ctx, map, {
      seed: hashSeed(map.id),
      potential: (u, v) =>
        potential[
          Math.min(H - 1, Math.max(0, Math.floor(v * res))) * W +
            Math.min(W - 1, Math.max(0, Math.floor(u * res)))
        ] ?? 0,
      shore: SHORE,
      elevation: elev,
      maxElevation: MAX_ELEVATION,
      fbm: makeNoise(hashSeed(map.id) ^ 0x7f4a7c15).fbm,
      project: (u, v, z) => [(K * (u - v) - x0) * pxPerCell, ((K * (u + v)) / 2 - z - y0) * pxPerCell],
      scale: pxPerCell,
      sprites,
    });
  }
  return { canvas, x: x0, y: y0, width: cw / pxPerCell, height: ch / pxPerCell };
}

function toCanvas(img: ImageData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  canvas.getContext('2d')?.putImageData(img, 0, 0);
  return canvas;
}

const cache = new WeakMap<MapDef, { fields: TerrainFields; iso?: IsoTerrain; flat?: HTMLCanvasElement }>();

function entry(map: MapDef) {
  let e = cache.get(map);
  if (!e) {
    e = { fields: terrainFields(map) };
    cache.set(map, e);
  }
  return e;
}

/** The isometric terrain of a map with its props, made once and shared. */
export function isoTerrainOf(map: MapDef, sprites: TerrainSprites): IsoTerrain {
  const e = entry(map);
  e.iso ??= isoTerrain(map, e.fields, sprites);
  return e.iso;
}

/** The flat terrain of a map (for the minimap), made once and shared. */
export function flatTerrainOf(map: MapDef): HTMLCanvasElement {
  const e = entry(map);
  e.flat ??= flatTerrain(e.fields);
  return e.flat;
}
